import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import PageShell from "../components/layout/PageShell";
import {
    createWorkspaceUser,
    disableWorkspaceUser,
    getWorkspaceUsers,
    updateWorkspaceUserRole
} from "../services/crmService";
import { canManageAccounts } from "../services/authService";
import "./TeamManagementPage.css";

const emptyForm = {
    name: "",
    email: "",
    password: "",
    roleKey: "ClientEditor"
};

function normalizeError(error, fallback) {
    return (
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        fallback
    );
}

function RoleBadge({ role }) {
    return <span className={`team-role-badge ${String(role || "").toLowerCase()}`}>{role || "Viewer"}</span>;
}

function StatusBadge({ active }) {
    return (
        <span className={`team-status-badge ${active ? "active" : "inactive"}`}>
            {active ? "Active" : "Inactive"}
        </span>
    );
}

export default function TeamManagementPage() {
    const canManage = canManageAccounts();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [search, setSearch] = useState("");

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getWorkspaceUsers();
            setUsers(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error(normalizeError(error, "Could not load team members."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const filteredUsers = useMemo(() => {
        const q = search.trim().toLowerCase();

        if (!q) return users;

        return users.filter((x) =>
            [
                x.name,
                x.email,
                x.roleKey,
                x.accountType
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(q)
        );
    }, [users, search]);

    const updateForm = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const closeCreate = () => {
        if (saving) return;
        setShowCreate(false);
        setForm(emptyForm);
    };

    const submitCreate = async (event) => {
        event.preventDefault();

        if (!form.name.trim()) {
            toast.error("Name is required.");
            return;
        }

        if (!form.email.trim()) {
            toast.error("Email is required.");
            return;
        }

        if (!form.password.trim()) {
            toast.error("Password is required.");
            return;
        }

        try {
            setSaving(true);

            await createWorkspaceUser({
                name: form.name.trim(),
                email: form.email.trim(),
                password: form.password,
                roleKey: form.roleKey
            });

            toast.success("Team member created.");
            setShowCreate(false);
            setForm(emptyForm);
            await loadUsers();
        } catch (error) {
            toast.error(normalizeError(error, "Could not create team member."));
        } finally {
            setSaving(false);
        }
    };

    const changeRole = async (userId, roleKey) => {
        try {
            setSaving(true);
            await updateWorkspaceUserRole(userId, roleKey);
            toast.success("Role updated.");
            await loadUsers();
        } catch (error) {
            toast.error(normalizeError(error, "Could not update role."));
        } finally {
            setSaving(false);
        }
    };

    const disableUser = async (user) => {
        if (!window.confirm(`Disable ${user.name || user.email}?`)) return;

        try {
            setSaving(true);
            await disableWorkspaceUser(user.id);
            toast.success("User disabled.");
            await loadUsers();
        } catch (error) {
            toast.error(normalizeError(error, "Could not disable user."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageShell title="Team">
            <div className="team-page">
                <section className="team-hero">
                    <div>
                        <span className="project-kicker">Client Workspace</span>
                        <h1>Team Members</h1>
                        <p>
                            Add users to your client workspace and control who can edit content,
                            manage CRM data, or only view information.
                        </p>
                    </div>

                    <div className="team-actions">
                        <button
                            type="button"
                            className="project-action-btn secondary"
                            onClick={loadUsers}
                            disabled={loading}
                        >
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>

                        {canManage && (
                            <button
                                type="button"
                                className="project-action-btn primary"
                                onClick={() => setShowCreate(true)}
                            >
                                Add Team Member
                            </button>
                        )}
                    </div>
                </section>

                <section className="team-summary-grid">
                    <div className="team-summary-card">
                        <span>Total Users</span>
                        <strong>{users.length}</strong>
                    </div>
                    <div className="team-summary-card">
                        <span>Active</span>
                        <strong>{users.filter((x) => x.isActive).length}</strong>
                    </div>
                    <div className="team-summary-card">
                        <span>Admins</span>
                        <strong>{users.filter((x) => x.roleKey === "AccountOwner" || x.roleKey === "ClientAdmin").length}</strong>
                    </div>
                    <div className="team-summary-card">
                        <span>Editors</span>
                        <strong>{users.filter((x) => x.roleKey === "ClientEditor").length}</strong>
                    </div>
                </section>

                <section className="team-panel">
                    <div className="team-panel-header">
                        <div>
                            <span className="project-kicker">Directory</span>
                            <h2>Workspace users</h2>
                            <p>Review access roles and disable users when needed.</p>
                        </div>

                        <div className="team-search">
                            <span>Search</span>
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search name, email, role..."
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="team-empty">Loading team members...</div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="team-empty">No team members found.</div>
                    ) : (
                        <div className="team-table-wrap">
                            <table className="team-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Last Login</th>
                                        <th></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredUsers.map((user) => (
                                        <tr key={user.id}>
                                            <td>
                                                <div className="team-user-cell">
                                                    <strong>{user.name}</strong>
                                                    <span>{user.email}</span>
                                                    {user.isOwner && <small>Workspace owner</small>}
                                                </div>
                                            </td>

                                            <td>
                                                {canManage && !user.isOwner ? (
                                                    <select
                                                        value={user.roleKey || "Viewer"}
                                                        disabled={saving}
                                                        onChange={(e) => changeRole(user.id, e.target.value)}
                                                    >
                                                        <option value="ClientAdmin">ClientAdmin</option>
                                                        <option value="ClientEditor">ClientEditor</option>
                                                        <option value="Viewer">Viewer</option>
                                                    </select>
                                                ) : (
                                                    <RoleBadge role={user.roleKey} />
                                                )}
                                            </td>

                                            <td>
                                                <StatusBadge active={user.isActive} />
                                            </td>

                                            <td>
                                                {user.createdAtUtc
                                                    ? new Date(user.createdAtUtc).toLocaleDateString()
                                                    : "-"}
                                            </td>

                                            <td>
                                                {user.lastLoginAtUtc
                                                    ? new Date(user.lastLoginAtUtc).toLocaleString()
                                                    : "-"}
                                            </td>

                                            <td>
                                                {canManage && !user.isOwner && user.isActive && (
                                                    <button
                                                        type="button"
                                                        className="team-danger-btn"
                                                        disabled={saving}
                                                        onClick={() => disableUser(user)}
                                                    >
                                                        Disable
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {showCreate && (
                    <div className="team-modal-backdrop">
                        <div className="team-modal">
                            <div className="team-modal-header">
                                <div>
                                    <span className="project-kicker">New Team Member</span>
                                    <h2>Add user</h2>
                                    <p>Create a login for another user in this client workspace.</p>
                                </div>

                                <button
                                    type="button"
                                    onClick={closeCreate}
                                    disabled={saving}
                                >
                                    ×
                                </button>
                            </div>

                            <form className="team-form" onSubmit={submitCreate}>
                                <label>
                                    <span>Name</span>
                                    <input
                                        value={form.name}
                                        onChange={(e) => updateForm("name", e.target.value)}
                                        placeholder="Team member name"
                                        autoFocus
                                    />
                                </label>

                                <label>
                                    <span>Email</span>
                                    <input
                                        value={form.email}
                                        onChange={(e) => updateForm("email", e.target.value)}
                                        placeholder="user@example.com"
                                    />
                                </label>

                                <label>
                                    <span>Temporary Password</span>
                                    <input
                                        type="password"
                                        value={form.password}
                                        onChange={(e) => updateForm("password", e.target.value)}
                                        placeholder="Set first login password"
                                    />
                                </label>

                                <label>
                                    <span>Role</span>
                                    <select
                                        value={form.roleKey}
                                        onChange={(e) => updateForm("roleKey", e.target.value)}
                                    >
                                        <option value="ClientAdmin">ClientAdmin</option>
                                        <option value="ClientEditor">ClientEditor</option>
                                        <option value="Viewer">Viewer</option>
                                    </select>
                                </label>

                                <div className="team-form-actions">
                                    <button
                                        type="button"
                                        className="project-action-btn secondary"
                                        onClick={closeCreate}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="project-action-btn primary"
                                        disabled={saving}
                                    >
                                        {saving ? "Creating..." : "Create User"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </PageShell>
    );
}